import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { ProductCategory } from '@/components/product-category'

type AppDetailCardProps = {
  logo: string
  mock: string
  icon: string
  category: 'people' | 'property'
  titleLead: string
  titlePrimary: string
  description: string
  /** Laptop/computer mockup: right-aligned instead of centered. */
  desktop?: boolean
  /** Dim + desaturate when a filter excludes this card's category. */
  dimmed?: boolean
  className?: string
}

export function AppDetailCard({
  logo,
  mock,
  icon,
  category,
  titleLead,
  titlePrimary,
  description,
  desktop = false,
  dimmed = false,
  className,
}: AppDetailCardProps) {
  return (
    <div
      className={cn(
        'bg-app-highlight-soft group relative flex h-full min-h-[30rem] cursor-pointer flex-col overflow-hidden rounded-3xl border border-muted p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl',
        dimmed && 'opacity-50 grayscale',
        className,
      )}
    >
      <img
        src={icon}
        alt=""
        width={300}
        height={300}
        loading="lazy"
        decoding="async"
        className="pointer-events-none absolute -top-28 -right-26 size-72 object-contain"
      />
      <ProductCategory
        category={category}
        size="sm"
        className="relative mb-4 self-start rounded-md bg-app-highlight-soft"
      />
      {/* ponytail: app logos vary in aspect ratio; h-7 w-auto reserves height,
          so no fixed width/height attrs here. */}
      <img
        src={logo}
        alt=""
        loading="lazy"
        decoding="async"
        className="relative h-7 w-auto max-w-[70%] self-start object-contain object-left"
      />
      <h3 className="relative mt-5 font-serif text-xl font-semibold tracking-tight text-accent-foreground">
        {titleLead}
        <span className="block text-primary">{titlePrimary}</span>
      </h3>
      <p className="relative mt-1 line-clamp-2 font-mono text-xs text-secondary-foreground">
        {description}
      </p>
      {/* Aspect-ratio window matching the device's opaque box (291x336 in the
          404x637 PNG) so the whole device is always framed, never cropped, at
          any card width. The image is scaled wider (device is ~72% of the PNG
          width) so it fills; the transparent margins bleed off and are clipped. */}
      <div
        className={cn(
          'relative mt-auto -mb-6 aspect-[291/336] overflow-hidden',
          // Laptop window bleeds into the right padding so the device sits flush
          // against the card's right border; phone window stays inside the padding.
          desktop ? 'w-[calc(100%+1.5rem)]' : 'w-full',
        )}
      >
        <img
          src={mock}
          alt=""
          width={404}
          height={637}
          loading="lazy"
          decoding="async"
          className={cn(
            'pointer-events-none absolute bottom-0 max-w-none object-contain object-bottom',
            // Phone: centered and scaled so the device (~72% of the PNG width)
            // fills. Laptop: right-aligned (device runs to the PNG's right edge)
            // and scaled a bit less (device is ~87% of the PNG width).
            desktop ? 'right-0 w-[115%]' : 'left-1/2 w-[139%] -translate-x-1/2',
          )}
        />
      </div>
      <span className="absolute right-4 bottom-4 flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted transition-colors group-hover:bg-muted/50">
        <ArrowRight className="size-5 text-primary" />
      </span>
    </div>
  )
}
